import { useState } from 'react'
import { Link, useLoaderData } from 'react-router'
import { InfoCard } from '#app/components/ui/info-card.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import {
	loadEventResults,
	type SoloAward,
	type GroupAward,
	type CollectiveAward,
} from '#app/utils/load-event-results.server.ts'
import { type Route } from './+types/tour.$slug_.results.ts'

export const meta: Route.MetaFunction = ({ data }) => {
	const eventName = data?.eventName ?? 'Event'
	return [
		{ title: `Results | ${eventName} | DanceMakers` },
		{
			name: 'description',
			content: `View competition results for DanceMakers ${eventName}.`,
		},
	]
}

// Format event date range using UTC to avoid timezone issues
function formatEventDates(
	startDate: string | null,
	endDate: string | null,
	fallback: string,
): string {
	if (!startDate) return fallback

	const start = new Date(startDate)
	const end = endDate ? new Date(endDate) : null

	const startMonth = start.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const startDay = start.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})

	if (!end) {
		return `${startMonth} ${startDay}`
	}

	const endMonth = end.toLocaleDateString('en-US', {
		month: 'short',
		timeZone: 'UTC',
	})
	const endDay = end.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: 'UTC',
	})

	if (startMonth === endMonth) {
		return `${startMonth} ${startDay}-${endDay}`
	}

	return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
}

export async function loader({ params }: Route.LoaderArgs) {
	const { slug } = params
	return loadEventResults(slug, { tourType: 'active' })
}

// Extract age group from award name (e.g., "Junior 1st Place" -> "Junior")
function getAgeGroup(awardName: string): string {
	const ageGroups = ['Junior', 'Pre-Teen', 'Teen', 'Senior', 'Camp Dance']
	for (const group of ageGroups) {
		if (awardName.toLowerCase().startsWith(group.toLowerCase())) {
			return group
		}
	}
	return ''
}

// Detect if a group award is an overall for its age group
function isGroupOverallAward(award: GroupAward): boolean {
	// Prefer explicit flag from API when available
	const anyAward = award as unknown as { groupOverall?: boolean }
	if (typeof anyAward.groupOverall === 'boolean') {
		return anyAward.groupOverall
	}

	// Fallback: infer from award name (e.g. "Teen High Score")
	const name = award.award
	const ageGroup = getAgeGroup(name)

	// Must have an age group and mention "high score"
	if (!ageGroup || !name.toLowerCase().includes('high score')) {
		return false
	}

	// The overall award should match exactly "{AgeGroup} High Score"
	// (case insensitive, with optional whitespace)
	// Category-specific high scores like "Teen Production High Score" or
	// "Teen Extended Line High Score" should NOT match
	const normalizedName = name.toLowerCase().trim()
	// const expectedPattern = `${ageGroup.toLowerCase()} high score`

	// Check if it matches the exact pattern (allowing for whitespace variations)
	const patternRegex = new RegExp(
		`^${ageGroup.toLowerCase()}\\s+high\\s+score$`,
		'i',
	)
	return patternRegex.test(normalizedName)
}

// Ensure group overall awards appear at the end of each age group section
function sortGroupAwardsForDisplay(awards: GroupAward[]): GroupAward[] {
	if (!awards || awards.length === 0) return awards

	const groups: Record<string, GroupAward[]> = {}
	const groupOrder: string[] = []

	for (const award of awards) {
		const ageGroup = getAgeGroup(award.award)
		if (!groups[ageGroup]) {
			groups[ageGroup] = []
			groupOrder.push(ageGroup)
		}
		groups[ageGroup].push(award)
	}

	const result: GroupAward[] = []

	for (const ageGroup of groupOrder) {
		const items = groups[ageGroup] ?? []
		const regularAwards = items.filter((a) => !isGroupOverallAward(a))
		const overallAwards = items.filter((a) => isGroupOverallAward(a))

		// Preserve original relative order within each subset
		result.push(...regularAwards, ...overallAwards)
	}

	return result
}

function AwardTable({
	awards,
	columns,
	minWidth,
}: {
	awards: (SoloAward | GroupAward)[]
	columns: { key: string; label: string }[]
	minWidth?: string
}) {
	if (awards.length === 0) {
		return (
			<div className="py-12 text-center text-muted-foreground">
				No awards to display yet.
			</div>
		)
	}

	return (
		<div className="overflow-x-auto">
			<table className="w-full" style={minWidth ? { minWidth } : undefined}>
				<thead>
					<tr className="border-b-2 border-border">
						{columns.map((col) => (
							<th
								key={col.key}
								className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
								style={{ fontFamily: 'var(--font-display)' }}
							>
								{col.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{awards.map((award, index) => {
						const currentAgeGroup = getAgeGroup(award.award)
						const prevAward = awards[index - 1]
						const prevAgeGroup = prevAward
							? getAgeGroup(prevAward.award)
							: currentAgeGroup
						const isNewAgeGroup = index > 0 && currentAgeGroup !== prevAgeGroup

						return (
							<tr
								key={index}
								className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted ${isNewAgeGroup ? 'border-t-2 border-t-accent/50' : ''}`}
							>
								{columns.map((col) => (
									<td
										key={col.key}
										className={`px-2 py-2 text-[11px] md:px-5 md:py-4 md:text-sm ${col.key === 'routine' ? 'font-bold' : ''} text-foreground`}
									>
										{(award as Record<string, unknown>)[col.key] as string}
									</td>
								))}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

// One section of the Nationals Collective tab (The Collective / Collective JV).
// Renders nothing when empty so the tab can show a single combined empty state.
function CollectiveSection({
	title,
	recipients,
}: {
	title: string
	recipients: CollectiveAward[]
}) {
	if (recipients.length === 0) return null

	return (
		<div>
			<h3
				className="mb-4 text-xl font-bold uppercase sm:text-2xl"
				style={{ fontFamily: 'var(--font-display)' }}
			>
				{title}
			</h3>
			<div className="overflow-hidden border border-accent/20">
				<div className="overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="border-b-2 border-border">
								<th
									className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Recipient
								</th>
								<th
									className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Studio
								</th>
							</tr>
						</thead>
						<tbody>
							{recipients.map((recipient, index) => (
								<tr
									key={recipient.id}
									className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted`}
								>
									<td className="px-2 py-2 text-[11px] font-bold text-foreground md:px-5 md:py-4 md:text-sm">
										{`${recipient.firstName} ${recipient.lastName}`}
									</td>
									<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
										{recipient.organization || '-'}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

// Header title. Active pages carry the event name in the back link, so the
// heading is just "Event Results". Past pages instead put the tour name + years
// on the back link and surface the event name here as the heading, keeping the
// event identifiable without a /past-tour/{slug} detail page.
function ResultsHeading({
	isPast,
	eventName,
}: {
	isPast: boolean
	eventName: string
}) {
	return (
		<h1
			className="mt-2 text-3xl font-bold uppercase text-foreground md:text-5xl"
			style={{ fontFamily: 'var(--font-display)' }}
		>
			{isPast ? eventName : 'Event Results'}
		</h1>
	)
}

export default function EventResults() {
	const {
		slug,
		tourType,
		tourName,
		tourYears,
		eventName,
		eventDates,
		eventStartDate,
		eventEndDate,
		showWinners,
		showSolos,
		soloAwards,
		duoTrioAwards,
		groupAwards,
		studioAwards,
		collective,
		collectiveJV,
		scholarships,
		nationalsScholarships,
		isNationals,
		styleHighScores,
	} = useLoaderData<typeof loader>()

	const sortedGroupAwards = sortGroupAwardsForDisplay(groupAwards)

	// On past-tour pages the back link returns to the /past-tour list (there is
	// no /past-tour/{slug} detail page), and the Overview tab is hidden.
	const isPast = tourType === 'past'
	const backTo = isPast ? '/past-tour' : `/tour/${slug}`
	// Past back link carries the season (tour name + years); active carries the
	// event name (it links to that event's /tour/{slug} page).
	const tourLabel =
		tourYears && tourName ? `${tourYears} ${tourName}` : tourName
	const backLabel = isPast ? (tourLabel ?? 'Past Tour') : eventName

	// Nationals reveals solos independently of the full winner set: on Mon–Wed
	// (showSolos ON, showWinners OFF) the Solo tab is the only result shown. This
	// is Nationals-only so regional behavior is unchanged. Completion/VOD logic
	// elsewhere stays keyed on showWinners — never showSolos.
	const nationalsSoloOnly = isNationals && showSolos && !showWinners
	const showSoloTab = showWinners || nationalsSoloOnly
	const showCollectiveTab = isNationals && showWinners

	// Default to the relevant tab for the current reveal state.
	const defaultTab = nationalsSoloOnly
		? 'solo'
		: !showWinners && styleHighScores.length > 0
			? 'stylehighscore'
			: 'scholarships'
	const [activeTab, setActiveTab] = useState(defaultTab)

	// Coming soon until something is revealed: full winners, style high scores,
	// or (Nationals only) the independent solo release.
	if (!showWinners && styleHighScores.length === 0 && !nationalsSoloOnly) {
		return (
			<div className="min-h-screen pb-16 pt-24">
				{/* Compact Header */}
				<section className="border-b border-border/30 bg-card">
					<div className="container px-4 py-3 sm:px-6 md:px-16 md:py-4">
						<div className="flex items-start justify-between gap-3 sm:items-center">
							<div>
								<Link
									to={backTo}
									className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-accent"
								>
									<svg
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 19l-7-7 7-7"
										/>
									</svg>
									<span className="text-sm sm:text-base">{backLabel}</span>
								</Link>
								<ResultsHeading isPast={isPast} eventName={eventName} />
							</div>
							<span className="mt-1 whitespace-nowrap text-xs text-muted-foreground sm:mt-0">
								{formatEventDates(eventStartDate, eventEndDate, eventDates)}
							</span>
						</div>
					</div>
				</section>

				<section className="container px-4 py-8 sm:px-6 sm:py-12 md:px-16 md:py-16">
					<div className="py-6 text-center sm:py-10 md:py-12">
						<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center sm:mb-5 sm:h-16 sm:w-16 md:mb-6 md:h-20 md:w-20">
							<svg
								className="h-6 w-6 text-accent sm:h-8 sm:w-8 md:h-10 md:w-10"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						</div>
						<h2
							className="mb-2 text-lg font-bold uppercase sm:mb-3 sm:text-xl md:mb-4 md:text-2xl"
							style={{ fontFamily: 'var(--font-display)' }}
						>
							Results Coming Soon
						</h2>
						<p className="mx-auto max-w-md px-4 text-sm text-muted-foreground sm:px-0 sm:text-base">
							{isNationals
								? 'Nationals results are posted throughout the week as each ceremony wraps — Solo Winners on Sunday, Style High Scores on Wednesday morning, then all remaining awards directly following the Wrap It Ceremony!'
								: 'Event results will be posted here after the event. Check back soon!'}
						</p>
					</div>
				</section>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-background pb-16 pt-24">
			{/* Compact Header */}
			<section className="border-b border-border/30">
				<div className="container px-4 py-3 sm:px-6 md:px-16 md:py-4">
					<div className="flex items-start justify-between gap-3 sm:items-center">
						<div>
							<Link
								to={backTo}
								className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-accent"
							>
								<svg
									className="h-4 w-4"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15 19l-7-7 7-7"
									/>
								</svg>
								<span className="text-sm sm:text-base">{backLabel}</span>
							</Link>
							<ResultsHeading isPast={isPast} eventName={eventName} />
						</div>
						<span className="mt-1 whitespace-nowrap text-xs text-muted-foreground sm:mt-0">
							{formatEventDates(eventStartDate, eventEndDate, eventDates)}
						</span>
					</div>
				</div>
			</section>

			{/* Sticky Category Navigation */}
			<section className="sticky top-[72px] z-40 border-y border-border/50 bg-card/95 backdrop-blur-md">
				<div className="container px-6 py-2 md:px-16">
					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-none bg-transparent p-0">
							{styleHighScores.length > 0 && (
								<TabsTrigger
									value="stylehighscore"
									className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Style High Score
								</TabsTrigger>
							)}
							{showWinners && (
								<TabsTrigger
									value="scholarships"
									className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-foreground sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Scholarship
								</TabsTrigger>
							)}
							{showSoloTab && (
								<TabsTrigger
									value="solo"
									className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Solo
								</TabsTrigger>
							)}
							{showWinners && (
								<>
									<TabsTrigger
										value="duotrio"
										className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
										style={{ fontFamily: 'var(--font-display)' }}
									>
										Duo/Trio
									</TabsTrigger>
									<TabsTrigger
										value="group"
										className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
										style={{ fontFamily: 'var(--font-display)' }}
									>
										Group
									</TabsTrigger>
									<TabsTrigger
										value="studio"
										className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
										style={{ fontFamily: 'var(--font-display)' }}
									>
										Studio
									</TabsTrigger>
								</>
							)}
							{showCollectiveTab && (
								<TabsTrigger
									value="collective"
									className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Collective
								</TabsTrigger>
							)}
							{showWinners && !isPast && (
								<TabsTrigger
									value="overview"
									className="border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base"
									style={{ fontFamily: 'var(--font-display)' }}
								>
									Overview
								</TabsTrigger>
							)}
						</TabsList>
					</Tabs>
				</div>
			</section>

			{/* Results Content */}
			<section className="container px-6 py-10 md:px-16">
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					{/* Style High Score Tab */}
					{styleHighScores.length > 0 && (
						<TabsContent value="stylehighscore" className="mt-0">
							{/* Nationals tags each entry with a tier (Style High Score vs.
							    Runner-Up); regionals have winners only, so the column is
							    hidden when no entry carries an shs value. */}
							{(() => {
								const showTier = styleHighScores.some((shs) => shs.shs)
								return (
									<div className="overflow-hidden border border-accent/20">
										<div className="overflow-x-auto">
											<table className="w-full" style={{ minWidth: '500px' }}>
												<thead>
													<tr className="border-b-2 border-border">
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Category
														</th>
														{showTier && (
															<th
																className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
																style={{ fontFamily: 'var(--font-display)' }}
															>
																Award
															</th>
														)}
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Routine
														</th>
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Studio
														</th>
													</tr>
												</thead>
												<tbody>
													{styleHighScores.map((shs, index) => (
														<tr
															key={index}
															className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted`}
														>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{shs.category}
															</td>
															{showTier && (
																<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																	{shs.shs}
																</td>
															)}
															<td className="px-2 py-2 text-[11px] font-bold text-foreground md:px-5 md:py-4 md:text-sm">
																{shs.name}
															</td>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{shs.studio}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								)
							})()}
						</TabsContent>
					)}

					{/* Overview Tab */}
					{showWinners && !isPast && (
						<TabsContent value="overview" className="mt-0 space-y-8">
							{isNationals ? (
								<InfoCard
									title="Nationals Awards"
									icon={
										<svg
											className="h-5 w-5"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
											strokeWidth={2}
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
											/>
										</svg>
									}
								>
									<p className="mb-4">
										There's a lot to celebrate at Nationals. For a complete
										breakdown of every award and how it's earned — from
										adjudication scores to Overalls, Style High Scores,
										scholarships, and The Collective — explore the full
										Nationals Awards guide.
									</p>
									<Link
										to="/nationals/awards"
										className="text-sm font-medium text-accent hover:text-accent/80"
									>
										Explore the Nationals Awards guide →
									</Link>
								</InfoCard>
							) : (
								<>
									<div className="grid gap-6 md:grid-cols-2">
										<InfoCard
											title="Solo Awards"
											icon={
												<svg
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
													/>
												</svg>
											}
										>
											<p className="mb-4">
												All soloists receive an adjudication score on their
												scoresheets. Scoresheets can be viewed in the
												registration online portal starting Sunday at 9 am. All
												Soloists receive their Solo Participation Award
												following their performance. All Overall Awards are
												presented Sunday at the Wrap It Ceremony.
											</p>
											{showSolos && (
												<button
													onClick={() => setActiveTab('solo')}
													className="text-sm font-medium text-accent hover:text-accent/80"
												>
													View Solo Results →
												</button>
											)}
										</InfoCard>

										<InfoCard
											title="Group Awards"
											icon={
												<svg
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
													/>
												</svg>
											}
										>
											<p className="mb-4">
												Each group routine is presented Adjudication Awards
												(High 1st, 1st, High 2nd, 2nd, 3rd) & Style High Scores
												at the routine's designated Awards Ceremony. All Overall
												Awards are presented Sunday at the Wrap It Ceremony.
											</p>
											<button
												onClick={() => setActiveTab('group')}
												className="text-sm font-medium text-accent hover:text-accent/80"
											>
												View Group Results →
											</button>
										</InfoCard>

										<InfoCard
											title="DanceAgain! Awards"
											icon={
												<svg
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
													/>
												</svg>
											}
										>
											DanceMakers gives out DanceAgain! Awards to select
											performances from competition that are invited to perform
											at the Wrap It Ceremony. All DanceAgains! are announced on
											Saturday night at the final award ceremony.
										</InfoCard>

										<InfoCard
											title="Wrap It Ceremony"
											icon={
												<svg
													className="h-5 w-5"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
													strokeWidth={2}
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
													/>
												</svg>
											}
										>
											The Wrap It Ceremony is where all solo, duo/trio and group
											Overall Awards as well as scholarships are announced!
											Special performances and DanceAgain performances are also
											featured! Everyone welcome - no wristbands required.
										</InfoCard>
									</div>

									<InfoCard
										title="Judging Commentary"
										icon={
											<svg
												className="h-5 w-5"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
												strokeWidth={2}
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
												/>
											</svg>
										}
									>
										All scores are uploaded by Sunday at 9 am and all critiques
										are uploaded by the Tuesday following your event, at 9 am
										EST to the registration online portal.
									</InfoCard>
								</>
							)}
						</TabsContent>
					)}

					{/* Solo Tab (reveals independently for Nationals via showSolos) */}
					{showSoloTab && (
						<TabsContent value="solo" className="mt-0">
							<div className="overflow-hidden border border-accent/20">
								<AwardTable
									awards={soloAwards}
									columns={[
										{ key: 'award', label: 'Award' },
										{ key: 'routine', label: 'Routine' },
										{ key: 'student', label: 'Dancer' },
										{ key: 'studio', label: 'Studio' },
									]}
									minWidth="600px"
								/>
							</div>
						</TabsContent>
					)}

					{/* Duo/Trio Tab */}
					{showWinners && (
						<TabsContent value="duotrio" className="mt-0">
							<div className="overflow-hidden border border-accent/20">
								<AwardTable
									awards={duoTrioAwards}
									columns={[
										{ key: 'award', label: 'Award' },
										{ key: 'routine', label: 'Routine' },
										{ key: 'studio', label: 'Studio' },
									]}
								/>
							</div>
						</TabsContent>
					)}

					{/* Group Tab */}
					{showWinners && (
						<TabsContent value="group" className="mt-0">
							<div className="overflow-hidden border border-accent/20">
								<AwardTable
									awards={sortedGroupAwards}
									columns={[
										{ key: 'award', label: 'Award' },
										{ key: 'routine', label: 'Routine' },
										{ key: 'studio', label: 'Studio' },
									]}
								/>
							</div>
						</TabsContent>
					)}

					{/* Studio Tab */}
					{showWinners && (
						<TabsContent value="studio" className="mt-0">
							<div className="overflow-hidden border border-accent/20">
								{studioAwards.filter((a) => a.studio).length > 0 ? (
									<div className="overflow-x-auto">
										<table className="w-full">
											<thead>
												<tr className="border-b-2 border-border">
													<th
														className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
														style={{ fontFamily: 'var(--font-display)' }}
													>
														Award
													</th>
													<th
														className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
														style={{ fontFamily: 'var(--font-display)' }}
													>
														Studio
													</th>
													<th
														className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
														style={{ fontFamily: 'var(--font-display)' }}
													>
														Recipient
													</th>
												</tr>
											</thead>
											<tbody>
												{studioAwards
													.filter((a) => a.studio)
													.map((award, index) => (
														<tr
															key={index}
															className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted`}
														>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{award.award}
															</td>
															<td className="px-2 py-2 text-[11px] font-bold text-foreground md:px-5 md:py-4 md:text-sm">
																{award.studio}
															</td>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{award.owner &&
																award.owner !== 'undefined undefined'
																	? award.owner
																	: '-'}
															</td>
														</tr>
													))}
											</tbody>
										</table>
									</div>
								) : (
									<div className="py-12 text-center text-muted-foreground">
										No studio awards to display yet.
									</div>
								)}
							</div>
						</TabsContent>
					)}

					{/* Collective Tab (Nationals) */}
					{showCollectiveTab && (
						<TabsContent value="collective" className="mt-0">
							<div className="space-y-8">
								<CollectiveSection
									title="The Collective"
									recipients={collective}
								/>
								<CollectiveSection
									title="Collective JV"
									recipients={collectiveJV}
								/>
								{collective.length === 0 && collectiveJV.length === 0 && (
									<div className="py-12 text-center text-muted-foreground">
										No Collective awards to display yet.
									</div>
								)}
							</div>
						</TabsContent>
					)}

					{/* Scholarships Tab */}
					{showWinners && (
						<TabsContent value="scholarships" className="mt-0">
							<div className="space-y-8">
								{scholarships.length > 0 && (
									<div className="overflow-hidden border border-accent/20">
										<div className="overflow-x-auto">
											<table className="w-full">
												<thead>
													<tr className="border-b-2 border-border">
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Award
														</th>
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Recipient
														</th>
														<th
															className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
															style={{ fontFamily: 'var(--font-display)' }}
														>
															Studio
														</th>
													</tr>
												</thead>
												<tbody>
													{scholarships.map((scholarship, index) => (
														<tr
															key={scholarship.id}
															className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted`}
														>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{scholarship.name}
															</td>
															<td className="px-2 py-2 text-[11px] font-bold text-foreground md:px-5 md:py-4 md:text-sm">
																{scholarship.attendee
																	? `${scholarship.attendee.firstName} ${scholarship.attendee.lastName}`
																	: '-'}
															</td>
															<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																{scholarship.attendee?.registration.user
																	.organization || '-'}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								)}

								{isNationals && nationalsScholarships.length > 0 && (
									<div>
										<h3
											className="mb-4 text-xl font-bold uppercase sm:text-2xl"
											style={{ fontFamily: 'var(--font-display)' }}
										>
											Nationals Scholarships
										</h3>
										<div className="overflow-hidden border border-accent/20">
											<div className="overflow-x-auto">
												<table className="w-full">
													<thead>
														<tr className="border-b-2 border-border">
															<th
																className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
																style={{ fontFamily: 'var(--font-display)' }}
															>
																Award
															</th>
															<th
																className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
																style={{ fontFamily: 'var(--font-display)' }}
															>
																Recipient
															</th>
															<th
																className="px-2 py-2 text-left text-xs font-bold uppercase tracking-wider md:px-5 md:py-4 md:text-base"
																style={{ fontFamily: 'var(--font-display)' }}
															>
																Studio
															</th>
														</tr>
													</thead>
													<tbody>
														{nationalsScholarships.map((scholarship, index) => (
															<tr
																key={scholarship.id}
																className={`${index % 2 === 0 ? 'bg-transparent' : 'bg-muted/50'} border-b border-border/30 transition-colors hover:bg-muted`}
															>
																<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																	{scholarship.name}
																</td>
																<td className="px-2 py-2 text-[11px] font-bold text-foreground md:px-5 md:py-4 md:text-sm">
																	{scholarship.attendee
																		? `${scholarship.attendee.firstName} ${scholarship.attendee.lastName}`
																		: '-'}
																</td>
																<td className="px-2 py-2 text-[11px] text-foreground md:px-5 md:py-4 md:text-sm">
																	{scholarship.attendee?.registration.user
																		.organization || '-'}
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}

								{scholarships.length === 0 &&
									nationalsScholarships.length === 0 && (
										<div className="py-12 text-center text-muted-foreground">
											No scholarships to display yet.
										</div>
									)}
							</div>
						</TabsContent>
					)}
				</Tabs>
			</section>
		</div>
	)
}
